








































































































































































































import Vue, { PropType } from "vue";
import iwInputSelect from "@/shared/components/buildingBlocks/InputSelect.vue";
import iwRegenerateModal from "./RegenerateModal.vue";
import { mapGetters } from "vuex";
import { generateContent, translateGeneratedContent } from "@/shared/scripts/aiGeneratedContent";
import { TranslateGeneratedContentPayload, TranslationContent, Language } from "@/shared/interfaces/optimizedEncoding/GeneratedContent.interface";

const COOLDOWN_DURATION_SECONDS = 30;

export default Vue.extend({
    components: {
        iwInputSelect: iwInputSelect,
        iwRegenerateModal: iwRegenerateModal,
    },
    props: {
        languages: {
            type: Array as PropType<Language[]>,
            required: true,
        },
        descriptionStorePath: {
            type: String,
            required: true,
        },
        titleStorePath: {
            type: String,
            required: true,
        },
        activeTabLanguage: {
            type: String,
            required: true,
        },
        liveDescriptionContent: {
            type: Object as PropType<Record<string, string>>,
            required: false,
            default: function() { return {}; },
        },
    },
    data: function() {
        return {
            isGenerating: false as boolean,
            cooldownSecondsLeft: 0 as number,
            cooldownTimer: null as number | null,
            isDescriptionGenerated: false as boolean,
            previousDescription: null as Record<string, unknown> | null,
            availablePreviousDescription: false as boolean,
            isTranslating: false as boolean,
            isTitleLoading: false as boolean,
            hasUsedTranslation: false as boolean,
            isRegenerateModalOpen: false as boolean,
            selectedTranslationTarget: "" as string,
            translateResetCounter: 0 as number,
        };
    },
    computed: {
        ...mapGetters("FormModule", ["getValue"]),

        description: function(): Record<string, string> {
            return this.getValue(this.descriptionStorePath);
        },
        title: function():Record<string, string> {
            return this.getValue(this.titleStorePath);
        },
        criteria: function(): Record<string, unknown> {
            return this.$store.getters["FormModule/getCriteria"] || {};
        },
        currentLocale: function(): string {
            const locale = this.$i18n?.locale ? this.$i18n.locale : "en";
            return locale.substring(0, 2).toLowerCase();
        },
        generateButtonLabel: function(): string {
            return this.isDescriptionGenerated
                ? (this.$t("ai_regenerate_description") as string)
                : (this.$t("ai_generate_description") as string);
        },
        options: function(): Array<Record<string, unknown>> {
            const languageCodes = this.languages.map((lang) => lang.prefix.toLowerCase());
            const otherLocales = languageCodes.filter((code) => code !== this.activeTabLanguage);

            const pairLabel = otherLocales[0].toUpperCase() + " " + (this.$t("and") as string) + " " +
                otherLocales[1].toUpperCase() as string;

            const selectableLanguages = otherLocales.map((code) => ({
                label: this.$t("language_only", { code: code.toUpperCase() }) as string,
                value: code,
            }));

            return [
                { label: this.$t("translate_to"), value: "", disabled: true },
                { label: pairLabel, value: "pair" },
                ...selectableLanguages,
            ];
        },
        isCooldownActive: function(): boolean {
            return this.cooldownSecondsLeft > 0;
        },
        isTranslateDisabled: function(): boolean {
            const lang = this.activeTabLanguage;
            return !(this.description?.[lang] || this.liveDescriptionContent[lang]);
        },
        iconOpacity: function(): number {
            return (this.isCooldownActive || this.isGenerating) ? 0.4 : 1;
        },
        selectKey: function(): string {
            return `${this.activeTabLanguage}-${this.options.map(o => o.value).join(",")}-${this.translateResetCounter}`;
        }
    },
    beforeDestroy: function() {
        this.clearCooldown();
    },
    methods: {
        handleLanguageTranslationSelection: function(value: string) {
            this.selectedTranslationTarget = value;
        },
        triggerTranslation: function() {
            if (!this.selectedTranslationTarget) {
                return;
            }
            this.translateDescription(this.selectedTranslationTarget);
        },
        translateDescription: function(targetLanguage: string) {
            if (this.isTranslating) {
                return;
            }
            this.isTranslating = true;

            this.requestTranslation(targetLanguage)
                .then((translations) => {
                    this.applyTranslations(translations);
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    this.isTranslating = false;
                    this.resetTranslationSelection();
                });
            this.hasUsedTranslation = true;
        },
        resetTranslationSelection: function() {
            // Clear the chosen target and bump the key so the InputSelect remounts.
            // A fresh mount starts at currentPosition 0 and lands on the "translate to"
            // placeholder; feeding "" to the existing instance cannot (its skip-disabled
            // logic walks off the start of the option list).
            this.selectedTranslationTarget = "";
            this.translateResetCounter += 1;
        },
        requestTranslation: async function(
            targetLanguage: string
        ): Promise<Record<string, TranslationContent>> {
            try {
                let targets: string[];

                if (targetLanguage === "pair") {
                    targets = this.languages
                        .map((lang) => lang.prefix.toLowerCase())
                        .filter((code) => code !== this.activeTabLanguage);
                } else {
                    targets = [targetLanguage.toLowerCase()];
                }

                if (!targets.length) return {};

                const payload: TranslateGeneratedContentPayload = {
                    sourceLanguage: this.activeTabLanguage,
                    targetLanguages: targets,
                    description: this.description?.[this.activeTabLanguage] || "",
                    title: this.title?.[this.activeTabLanguage] || "",
                };

                const response = await translateGeneratedContent(payload);

                return response.translations ?? {};
            } catch (error) {
                this.dialog.alert(this.$t("server_not_responding_error").toString());
                return {};
            }
        },

        generateDescription: function() {
            if (this.isCooldownActive || this.isGenerating) {
                return;
            }

            const language = this.activeTabLanguage;

            if (this.hasUsedTranslation && this.isDescriptionGenerated) {
                this.isRegenerateModalOpen = true;
                return;
            }

            this.executeGeneration(language);
        },
        closeRegenerateModal: function() {
            this.isRegenerateModalOpen = false;
        },
        confirmRegenerate: function() {
            this.isRegenerateModalOpen = false;
            this.executeGeneration(this.activeTabLanguage);
        },
        executeGeneration: async function(language: string) {
            this.captureCurrentDescriptions();
            this.isGenerating = true;
            this.startCooldown();

            try {
                const descriptions = await this.requestAiDescription(language);
                this.applyDescriptions(descriptions);
                this.isDescriptionGenerated = true;
            } finally {
                this.hasUsedTranslation = false;
                this.isGenerating = false;
            }
        },

        startCooldown: function() {
            this.clearCooldown();
            this.cooldownSecondsLeft = COOLDOWN_DURATION_SECONDS;
            this.cooldownTimer = window.setInterval(() => {
                if (this.cooldownSecondsLeft <= 1) {
                    this.clearCooldown();
                    return;
                }
                this.cooldownSecondsLeft -= 1;
            }, 1000);
        },
        clearCooldown: function() {
            if (this.cooldownTimer !== null) {
                window.clearInterval(this.cooldownTimer);
                this.cooldownTimer = null;
            }
            this.cooldownSecondsLeft = 0;
        },
        revertToPrevious: function() {
            if (!this.previousDescription) {
                return;
            }
            const cleanSnapshot = JSON.parse(JSON.stringify(this.previousDescription));
            this.applyDescriptions(cleanSnapshot);
            this.previousDescription = null;
            this.availablePreviousDescription = false;
            this.$emit("revert-description", cleanSnapshot);
        },
        captureCurrentDescriptions: function() {
            if (!this.description || typeof this.description !== "object") {
                this.previousDescription = null;
                this.availablePreviousDescription = false;
                return;
            }
            const snapshot: Record<string, unknown> = {};
            this.languages.forEach((lang) => {
                const code = lang.prefix.toLowerCase();
                snapshot[code] = this.description?.[code];
            });
            this.previousDescription = JSON.parse(JSON.stringify(snapshot));
            this.availablePreviousDescription = true;

        },
        requestAiDescription: async function(language: string): Promise<Record<string, string>> {
            const classifiedId = this.$store.getters["FormModule/getClassifiedId"];

            if (!classifiedId) {
                return { [language]: "" };
            }

            try {
                const response = await generateContent({
                    classifiedId: classifiedId,
                    fields: ["description"],
                    language: language
                });

                return response?.description ? { [language]: response.description } : { [language]: "" };
            } catch (error) {
                this.dialog.alert(this.$t("server_not_responding_error").toString());
                // Return empty fallback so UI doesn't break
                return { [language]: "" };
            }
        },

        applyDescriptions: function(descriptions: Record<string, unknown>) {
            if (!descriptions) {
                return;
            }

            Object.keys(descriptions).forEach((code) => {
                this.$store.dispatch("FormModule/addValue", {
                    path: `${this.descriptionStorePath}.${code}`,
                    value: descriptions[code],
                });
            });

            this.$emit("descriptions-updated", descriptions);
        },

        applyTranslations: function(translations: Record<string, TranslationContent>) {
            if (!translations) return;

            Object.keys(translations).forEach((code) => {
                const translation = translations[code];
                // Update description in the store
                if (translation?.description) {
                    this.$store.dispatch("FormModule/addValue", {
                        path: `${this.descriptionStorePath}.${code}`,
                        value: translation.description,
                    });
                }
                // Update title in the store
                if (translation?.title) {
                    this.$store.dispatch("FormModule/addValue", {
                        path: `${this.titleStorePath}.${code}`,
                        value: translation.title,
                    });
                }
            });

            this.$emit("translations-updated", translations);
        },
    }
});
